import type { FC } from 'react';
import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import { differenceInDays } from 'date-fns';

import Button from '@atlaskit/button/new';

import { PremiumTrialOnboardingModal } from '@confluence/premium-onboarding';
import { TriggerFrom } from '@confluence/premium-onboarding/entry-points/PremiumTrialOnboardingModal';
import { useSessionData, AccessStatus } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { ErrorDisplay } from '@confluence/error-boundary';

import { TrialDetailsQuery } from '../TrialDetailsQuery.graphql';
import type { TrialDetailsQuery as TrialDetailsQueryType } from '../__types__/TrialDetailsQuery';

const getTrialDaysLeft = (trialEndDate: string | null | undefined) => {
	if (!trialEndDate) {
		return null;
	}

	// +1 day to round up for fractional days
	return differenceInDays(new Date(trialEndDate), new Date()) + 1;
};

export const HTV2ExplorePremiumCTA: FC<{ onClick: () => void }> = ({ children, onClick }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { accessStatus, isLoggedIn, edition } = useSessionData();
	const isLicensed = accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;
	const skipTrialDetailsQuery = Boolean(
		!isLoggedIn || !isLicensed || edition !== ConfluenceEdition.PREMIUM,
	);

	const {
		data: trialDetailsData,
		error: trialDetailsError,
		loading: trialDetailsLoading,
	} = useQuery<TrialDetailsQueryType>(TrialDetailsQuery, {
		skip: skipTrialDetailsQuery,
	});

	const formattedTrialsDaysLeft = getTrialDaysLeft(trialDetailsData?.license?.trialEndDate) ?? 0;

	const handleClick = () => {
		setIsModalOpen((isModalOpen) => !isModalOpen);
		onClick();
	};

	return (
		<>
			<>{trialDetailsError && <ErrorDisplay error={trialDetailsError} />}</>
			{isModalOpen && (
				<PremiumTrialOnboardingModal
					onClose={() => setIsModalOpen(false)}
					triggerFrom={TriggerFrom.HOME_ONBOARDING_TAKEOVER_V2}
					daysLeft={formattedTrialsDaysLeft}
				/>
			)}
			<Button
				isDisabled={Boolean(
					formattedTrialsDaysLeft <= 0 || skipTrialDetailsQuery || trialDetailsError,
				)}
				isLoading={trialDetailsLoading}
				onClick={handleClick}
				appearance="default"
			>
				{children}
			</Button>
		</>
	);
};
